/**
 * @file useReceiptDocument.ts
 * @description Данные карточки приёмки: загрузка, строки, шапка черновика и
 * «Указать срок» (phase19.23, A6 — симметрично `useWriteoffDocument`;
 * причина приёмки и срок — phase19.22, W17-9b, W20).
 *
 * Вынесено из страницы (SRP): странице остаётся раскладка документа, хуку —
 * запросы. Проведение с предпроверкой срока — `useReceiptCommit`. После
 * мутаций документ перечитываем с сервера, а не правим руками.
 */

import { useCallback, useEffect, useState } from "react";

import { useNotify } from "../ui/NotifyProvider";
import type { ReceiptItemPatch } from "./receipt-item-edit";
import type { ReceiptItemPayload } from "./ReceiptItemForm";
import { receiptsApi, type GoodsReceiptDto, type GoodsReceiptReason } from "../../lib/api-wms";
import { wmsErrorText } from "../../lib/wms-error-text";

export function useReceiptDocument(id: string | undefined, isNew: boolean) {
  const notify = useNotify();
  const [receipt, setReceipt] = useState<GoodsReceiptDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (isNew || !id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    receiptsApi
      .findOne(id)
      .then((data) => {
        if (cancelled) return;
        setReceipt(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить приёмку"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  /** Перечитать документ с сервера — один источник правды. */
  const reload = useCallback(async (receiptId: string) => {
    setReceipt(await receiptsApi.findOne(receiptId));
  }, []);

  const addItem = useCallback(
    async (payload: ReceiptItemPayload): Promise<boolean> => {
      if (!receipt) return false;
      setAdding(true);
      try {
        await receiptsApi.addItem(receipt.id, payload);
        await reload(receipt.id);
        return true;
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось добавить позицию"));
        return false;
      } finally {
        setAdding(false);
      }
    },
    [receipt, notify, reload],
  );

  const removeItem = useCallback(
    async (itemId: string) => {
      if (!receipt) return;
      try {
        await receiptsApi.removeItem(receipt.id, itemId);
        await reload(receipt.id);
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось удалить позицию"));
      }
    },
    [receipt, notify, reload],
  );

  /** Причина приёмки и парное списание (только черновик, W17-9b). */
  const updateReason = useCallback(
    async (reason: GoodsReceiptReason, parentWriteoffId: string | null) => {
      if (!receipt) return;
      try {
        await receiptsApi.update(receipt.id, { reason, parentWriteoffId });
        await reload(receipt.id);
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось сохранить причину приёмки"));
      }
    },
    [receipt, notify, reload],
  );

  /**
   * Правка строки черновика (`PATCH …/items/:itemId`, phase19.31 Б16):
   * «Изменить позицию», «Указать ячейку», «Заполнить сроки». Ошибку сервера
   * показываем его текстом (`wmsErrorText`), документ перечитываем.
   */
  const updateItem = useCallback(
    async (itemId: string, patch: ReceiptItemPatch, failText = "Не удалось сохранить позицию"): Promise<boolean> => {
      if (!receipt) return false;
      try {
        await receiptsApi.updateItem(receipt.id, itemId, patch);
        await reload(receipt.id);
        return true;
      } catch (err) {
        notify.error(wmsErrorText(err, failText));
        return false;
      }
    },
    [receipt, notify, reload],
  );

  /** «Заполнить сроки» в черновике: правка дат строки. */
  const updateItemDates = useCallback(
    (itemId: string, dates: { manufacturedAt: string | null; expiresAt: string | null }) =>
      updateItem(itemId, dates, "Не удалось сохранить срок строки"),
    [updateItem],
  );

  /** «Указать срок» у строки проведённой приёмки (W20: только пусто → значение). */
  const setItemExpiry = useCallback(
    async (itemId: string, expiresAt: string): Promise<boolean> => {
      if (!receipt) return false;
      try {
        await receiptsApi.setItemExpiry(receipt.id, itemId, expiresAt);
        notify.success("Срок указан — перепечатайте этикетки партии");
        await reload(receipt.id);
        return true;
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось указать срок"));
        return false;
      }
    },
    [receipt, notify, reload],
  );

  return {
    receipt,
    setReceipt,
    loading,
    error,
    adding,
    reload,
    addItem,
    removeItem,
    updateReason,
    updateItem,
    updateItemDates,
    setItemExpiry,
  };
}
